import { PaginationT } from 'models/Pagination'
import { api } from '../index'

export interface UserList {
  id: number
  name: string
  createdAt: string
}

interface RawUserList {
  id: number
  name: string
  created_at: string
}

interface GetUserListsResponse {
  user_lists: RawUserList[]
  pagination?: PaginationT
}

interface GetUserListsParams {
  page?: number
  per_page?: number
  query?: string
}

interface CreateUserListParams {
  name: string
}

interface CreateUserListResponse {
  user_list: RawUserList
}

export const userListsApi = api.injectEndpoints({
  endpoints: builder => ({
    getUserLists: builder.query<
      { data: UserList[]; pagination: PaginationT },
      GetUserListsParams | void
    >({
      query: params => ({
        url: 'user_lists',
        method: 'GET',
        params: params || undefined,
      }),
      transformResponse: (response: GetUserListsResponse) => ({
        data: (response.user_lists || []).map(list => ({
          id: list.id,
          name: list.name,
          createdAt: list.created_at,
        })),
        pagination: response.pagination || {
          total_pages: 1,
          per_page: 25,
          current_page: 1,
          total_count: response.user_lists?.length || 0,
        },
      }),
      providesTags: result => {
        const base = [{ type: 'UserList' as const, id: 'LIST' }]
        if (!result) return base
        return [
          ...result.data.map(({ id }) => ({ type: 'UserList' as const, id })),
          ...base,
        ]
      },
    }),

    createUserList: builder.mutation<UserList, CreateUserListParams>({
      query: body => ({
        url: 'user_lists',
        method: 'POST',
        body,
      }),
      transformResponse: (response: CreateUserListResponse): UserList => ({
        id: response.user_list.id,
        name: response.user_list.name,
        createdAt: response.user_list.created_at,
      }),
      invalidatesTags: [{ type: 'UserList', id: 'LIST' }],
    }),

    renameUserList: builder.mutation<UserList, { id: string; name: string }>({
      query: ({ id, name }) => ({
        url: `/user_lists/${id}`,
        method: 'PATCH',
        body: { name },
      }),
      transformResponse: (response: { user_list: RawUserList }): UserList => ({
        id: response.user_list.id,
        name: response.user_list.name,
        createdAt: response.user_list.created_at,
      }),
      invalidatesTags: ['UserList'],
    }),

    deleteUserList: builder.mutation<void, string>({
      query: id => ({
        url: `/user_lists/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserList'],
    }),
  }),
})

export const {
  useGetUserListsQuery,
  useCreateUserListMutation,
  useRenameUserListMutation,
  useDeleteUserListMutation,
} = userListsApi
